import { useEffect } from 'react';

/**
 * Globally stops the mouse wheel / trackpad from changing the value of a focused
 * <input type="number">. That browser default silently mangles amounts — e.g. a
 * pasted "95" on a SuperBill fee becomes "92" when the user scrolls the page with
 * the field still focused (each scroll notch decrements by the input's step).
 *
 * Why a native listener instead of an `onWheel` prop: React 17 registers `wheel`
 * as a passive listener at the root, so `e.preventDefault()` inside an `onWheel`
 * handler is ignored ("Unable to preventDefault inside passive event listener").
 * Attaching natively with `{ passive: false }` lets preventDefault actually work.
 *
 * Unlike blurring the field, focus is preserved. The page still scrolls normally
 * everywhere except directly over the currently-focused number input — the only
 * situation where the wheel would otherwise change the value.
 *
 * Invoke once at the highest common level of the app (App).
 */
export function useDisableNumberInputScroll(): void {
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      const active = document.activeElement;
      if (active instanceof HTMLInputElement && active.type === 'number' && event.target === active) {
        event.preventDefault();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);
}
